import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Form, Button, Alert, Spinner, Nav, Tab, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './informacion-negocio.scss';
import { Breadcrumb } from 'react-bootstrap';
import { getEmpresaInfo, getDepartamentos } from '../../../services/empresaService';
import { getProyectosEmpresa, getProyectoInfo, updateProyectoInfo } from '../../../services/proyectoService';

const InformacionNegocio = () => {
  const [empresaData, setEmpresaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departamentos] = useState(getDepartamentos());

  // Estados para proyectos
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [proyectoData, setProyectoData] = useState(null);
  const [loadingProyecto, setLoadingProyecto] = useState(false);
  const [errorProyecto, setErrorProyecto] = useState(null);
  const [errorCargarProyectos, setErrorCargarProyectos] = useState(null);
  const [guardandoProyecto, setGuardandoProyecto] = useState(false);
  const [activeTab, setActiveTab] = useState('empresa');

  // Estados para modal de advertencia de unidades disponibles
  const [showAdvertenciaUnidades, setShowAdvertenciaUnidades] = useState(false);
  const [valorUnidadesPendiente, setValorUnidadesPendiente] = useState(null);
  const [valorUnidadesOriginal, setValorUnidadesOriginal] = useState(null);
  const [unidadesEditadas, setUnidadesEditadas] = useState(false);

  // Cargar información de empresa y proyectos al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cargar información de empresa
        const response = await getEmpresaInfo();
        if (response.Status === 'Success') {
          setEmpresaData(response.data);
        } else {
          setError('Error al cargar la información de la empresa');
        }

        // Cargar lista de proyectos
        try {
          setErrorCargarProyectos(null);
          const proyectosList = await getProyectosEmpresa();
          setProyectos(proyectosList);

          // Si hay proyectos y no hay uno seleccionado, seleccionar el primero
          if (proyectosList.length > 0 && !proyectoSeleccionado) {
            const primerProyecto = proyectosList[0];
            setProyectoSeleccionado(primerProyecto.codigoProyecto);
          }
        } catch (error) {
          console.error('Error cargando proyectos:', error);
          setErrorCargarProyectos('No se pudo cargar la lista de proyectos. Por favor, intente nuevamente.');
          setProyectos([]);
        }
      } catch (error) {
        console.error('Error cargando información de empresa:', error);
        setError(error.message || 'Error al cargar la información de la empresa');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Cargar información del proyecto cuando se selecciona uno
  useEffect(() => {
    const loadProyectoInfo = async () => {
      if (!proyectoSeleccionado) {
        setProyectoData(null);
        return;
      }

      try {
        setLoadingProyecto(true);
        setErrorProyecto(null);

        const response = await getProyectoInfo(proyectoSeleccionado);
        if (response.Status === 'Success') {
          // Obtener el nombre del proyecto desde la lista
          const proyectoEncontrado = proyectos.find(p => (p.codigoProyecto || p.codigo_proyecto || p.id) === proyectoSeleccionado);
          const nombreProyecto = proyectoEncontrado?.nombreProyecto || proyectoEncontrado?.nombre_proyecto || proyectoEncontrado?.nombre || '';

          // Separar dirección y Google Maps para dirección del proyecto
          const direccionProyectoSeparada = separarDireccionYGoogleMaps(response.data.direccion_proyecto || '');
          // Separar dirección y Google Maps para dirección de sala de ventas
          const direccionSalaVentasSeparada = separarDireccionYGoogleMaps(response.data.direccion_sala_ventas || '');

          setProyectoData({
            ...response.data,
            nombre_proyecto: nombreProyecto,
            direccion_proyecto_texto: direccionProyectoSeparada.direccion,
            direccion_proyecto_maps: direccionProyectoSeparada.googleMaps,
            direccion_sala_ventas_texto: direccionSalaVentasSeparada.direccion,
            direccion_sala_ventas_maps: direccionSalaVentasSeparada.googleMaps
          });
          // Guardar el valor original de unidades disponibles
          setValorUnidadesOriginal(response.data.unidades_disponibles || '');
          setUnidadesEditadas(false);
        } else {
          setErrorProyecto('Error al cargar la información del proyecto');
        }
      } catch (error) {
        console.error('Error cargando información del proyecto:', error);
        setErrorProyecto(error.message || 'Error al cargar la información del proyecto');
        // Inicializar con datos vacíos si hay error
        const proyectoEncontrado = proyectos.find(p => (p.codigoProyecto || p.codigo_proyecto || p.id) === proyectoSeleccionado);
        // Inicializar horario con valores por defecto (array con un elemento)
        const horarioDefault = JSON.stringify([{
          num_dia_semana_inicio: 1,
          num_dia_semana_fin: 5,
          hora_inicio: '09:00:00',
          hora_fin: '18:00:00'
        }]);

        setProyectoData({
          codigo_proyecto: proyectoSeleccionado,
          nombre_proyecto: proyectoEncontrado?.nombreProyecto || proyectoEncontrado?.nombre_proyecto || proyectoEncontrado?.nombre || '',
          acerca_proyecto: '',
          fecha_estimada_entrega: '',
          tipos_unidades: '',
          exhibicion_unidades: '',
          etapa_actual: '',
          direccion_proyecto: '',
          direccion_proyecto_texto: '',
          direccion_proyecto_maps: '',
          direccion_sala_ventas: '',
          direccion_sala_ventas_texto: '',
          direccion_sala_ventas_maps: '',
          horario_atencion: horarioDefault,
          unidades_disponibles: '',
          recorrido_virtual: ''
        });
      } finally {
        setLoadingProyecto(false);
      }
    };

    loadProyectoInfo();
  }, [proyectoSeleccionado, proyectos]);

  // Manejar cambio de proyecto seleccionado
  const handleProyectoChange = (e) => {
    const codigoProyecto = e.target.value ? parseInt(e.target.value) : null;
    setProyectoSeleccionado(codigoProyecto);
  };

  // Funciones helper para separar y unir dirección + Google Maps
  const separarDireccionYGoogleMaps = (textoCompleto) => {
    if (!textoCompleto || textoCompleto.trim() === '') {
      return { direccion: '', googleMaps: '' };
    }

    const texto = textoCompleto.trim();

    // Verificar si el texto completo empieza con "Google Maps:"
    if (texto.startsWith('Google Maps:')) {
      const googleMaps = texto.substring('Google Maps:'.length).trim();
      return { direccion: '', googleMaps };
    }

    // Separar por "|"
    const partes = texto.split('|').map(p => p.trim()).filter(p => p !== '');

    if (partes.length === 0) {
      return { direccion: '', googleMaps: '' };
    }

    let direccion = '';
    let googleMaps = '';

    // Buscar la parte que contiene "Google Maps:"
    const parteGoogleMaps = partes.find(p => p.includes('Google Maps:'));

    if (parteGoogleMaps) {
      // Extraer el link (todo después de "Google Maps:")
      const linkIndex = parteGoogleMaps.indexOf('Google Maps:');
      googleMaps = parteGoogleMaps.substring(linkIndex + 'Google Maps:'.length).trim();

      // La otra parte es la dirección
      const partesDireccion = partes.filter(p => !p.includes('Google Maps:'));
      direccion = partesDireccion.join(' | ').trim();
    } else {
      // Si no hay "Google Maps:", todo es dirección
      direccion = partes.join(' | ').trim();
    }

    return { direccion, googleMaps };
  };

  const unirDireccionYGoogleMaps = (direccion, googleMaps) => {
    const dir = (direccion || '').trim();
    const maps = (googleMaps || '').trim();

    if (!dir && !maps) {
      return '';
    }

    if (dir && maps) {
      return `${dir} | Google Maps: ${maps}`;
    }

    if (dir && !maps) {
      return dir;
    }

    if (!dir && maps) {
      return `Google Maps: ${maps}`;
    }

    return '';
  };

  // Manejar cambios en los campos del proyecto
  const handleProyectoFieldChange = (field, value) => {
    setProyectoData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Parsear horario_atencion del backend (puede venir como string JSON, objeto único o array)
  const parseHorarioAtencion = (horario) => {
    // Función auxiliar para normalizar un objeto de horario
    const normalizarHorario = (h) => {
      return {
        num_dia_semana_inicio: h.num_dia_semana_inicio ?? h.dia_inicio ?? 1,
        num_dia_semana_fin: h.num_dia_semana_fin ?? h.dia_fin ?? 5,
        // Convertir HH:MM:SS a HH:MM para el input type="time"
        hora_inicio: h.hora_inicio ? (h.hora_inicio.length > 5 ? h.hora_inicio.substring(0, 5) : h.hora_inicio) : '09:00',
        hora_fin: h.hora_fin ? (h.hora_fin.length > 5 ? h.hora_fin.substring(0, 5) : h.hora_fin) : '18:00'
      };
    };

    if (!horario) {
      // Retornar array con un horario por defecto
      return [{
        num_dia_semana_inicio: 1, // Lunes por defecto
        num_dia_semana_fin: 5, // Viernes por defecto
        hora_inicio: '09:00',
        hora_fin: '18:00'
      }];
    }

    if (typeof horario === 'string') {
      try {
        const parsed = JSON.parse(horario);
        // Si es un array, normalizar cada elemento
        if (Array.isArray(parsed)) {
          return parsed.length > 0 ? parsed.map(normalizarHorario) : [{
            num_dia_semana_inicio: 1,
            num_dia_semana_fin: 5,
            hora_inicio: '09:00',
            hora_fin: '18:00'
          }];
        }
        // Si es un objeto único, convertirlo a array
        return [normalizarHorario(parsed)];
      } catch (e) {
        // Si no es JSON válido, retornar valores por defecto
        return [{
          num_dia_semana_inicio: 1,
          num_dia_semana_fin: 5,
          hora_inicio: '09:00',
          hora_fin: '18:00'
        }];
      }
    }

    // Si ya es un array, normalizar cada elemento
    if (Array.isArray(horario)) {
      return horario.length > 0 ? horario.map(normalizarHorario) : [{
        num_dia_semana_inicio: 1,
        num_dia_semana_fin: 5,
        hora_inicio: '09:00',
        hora_fin: '18:00'
      }];
    }

    // Si es un objeto único, convertirlo a array
    return [normalizarHorario(horario)];
  };

  // Obtener array de horarios actual
  const getHorariosArray = () => {
    return parseHorarioAtencion(proyectoData?.horario_atencion);
  };

  // Manejar cambios en el horario de atención (por índice)
  const handleHorarioChange = (index, field, value) => {
    const horarios = getHorariosArray();
    const nuevosHorarios = [...horarios];
    nuevosHorarios[index] = {
      ...nuevosHorarios[index],
      [field]: value
    };

    // Guardar como JSON string (el backend recibirá un array)
    handleProyectoFieldChange('horario_atencion', JSON.stringify(nuevosHorarios));
  };

  // Añadir un nuevo horario
  const handleAñadirHorario = () => {
    const horarios = getHorariosArray();
    const nuevoHorario = {
      num_dia_semana_inicio: 1,
      num_dia_semana_fin: 5,
      hora_inicio: '09:00',
      hora_fin: '18:00'
    };
    const nuevosHorarios = [...horarios, nuevoHorario];
    handleProyectoFieldChange('horario_atencion', JSON.stringify(nuevosHorarios));
  };

  // Eliminar un horario
  const handleEliminarHorario = (index) => {
    const horarios = getHorariosArray();
    if (horarios.length <= 1) {
      // No permitir eliminar si solo hay uno
      return;
    }
    const nuevosHorarios = horarios.filter((_, i) => i !== index);
    handleProyectoFieldChange('horario_atencion', JSON.stringify(nuevosHorarios));
  };

  // Manejar intento de edición de unidades disponibles
  const handleUnidadesDisponiblesChange = (e) => {
    const nuevoValor = e.target.value;

    // Validar longitud
    if (nuevoValor.length > 310) {
      return;
    }

    // Si ya se aceptó la edición anteriormente, permitir edición normal
    if (unidadesEditadas) {
      handleProyectoFieldChange('unidades_disponibles', nuevoValor);
      return;
    }

    // Si el valor es diferente al original, mostrar advertencia
    if (nuevoValor !== valorUnidadesOriginal) {
      setValorUnidadesPendiente(nuevoValor);
      setShowAdvertenciaUnidades(true);
    } else {
      // Si vuelve al valor original, permitir edición normal
      handleProyectoFieldChange('unidades_disponibles', nuevoValor);
    }
  };

  // Aceptar la edición de unidades disponibles
  const handleAceptarEdicionUnidades = () => {
    if (valorUnidadesPendiente !== null) {
      handleProyectoFieldChange('unidades_disponibles', valorUnidadesPendiente);
      setUnidadesEditadas(true);
    }
    setShowAdvertenciaUnidades(false);
    setValorUnidadesPendiente(null);
  };

  // Cancelar la edición de unidades disponibles
  const handleCancelarEdicionUnidades = () => {
    // Restaurar el valor original si se cancela
    if (valorUnidadesOriginal !== null) {
      handleProyectoFieldChange('unidades_disponibles', valorUnidadesOriginal);
    }
    setShowAdvertenciaUnidades(false);
    setValorUnidadesPendiente(null);
  };

  // Recargar información del proyecto
  const recargarProyectoInfo = async () => {
    if (!proyectoSeleccionado) {
      return;
    }

    try {
      setLoadingProyecto(true);
      setErrorProyecto(null);

      const response = await getProyectoInfo(proyectoSeleccionado);
      if (response.Status === 'Success') {
        // Obtener el nombre del proyecto desde la lista
        const proyectoEncontrado = proyectos.find(p => (p.codigoProyecto || p.codigo_proyecto || p.id) === proyectoSeleccionado);
        const nombreProyecto = proyectoEncontrado?.nombreProyecto || proyectoEncontrado?.nombre_proyecto || proyectoEncontrado?.nombre || '';

        // Separar dirección y Google Maps para dirección del proyecto
        const direccionProyectoSeparada = separarDireccionYGoogleMaps(response.data.direccion_proyecto || '');
        // Separar dirección y Google Maps para dirección de sala de ventas
        const direccionSalaVentasSeparada = separarDireccionYGoogleMaps(response.data.direccion_sala_ventas || '');

        setProyectoData({
          ...response.data,
          nombre_proyecto: nombreProyecto,
          direccion_proyecto_texto: direccionProyectoSeparada.direccion,
          direccion_proyecto_maps: direccionProyectoSeparada.googleMaps,
          direccion_sala_ventas_texto: direccionSalaVentasSeparada.direccion,
          direccion_sala_ventas_maps: direccionSalaVentasSeparada.googleMaps
        });
        // Guardar el valor original de unidades disponibles
        setValorUnidadesOriginal(response.data.unidades_disponibles || '');
        setUnidadesEditadas(false);
      } else {
        setErrorProyecto('Error al cargar la información del proyecto');
      }
    } catch (error) {
      console.error('Error cargando información del proyecto:', error);
      setErrorProyecto(error.message || 'Error al cargar la información del proyecto');
    } finally {
      setLoadingProyecto(false);
    }
  };

  // Guardar información del proyecto
  const handleGuardarProyecto = async () => {
    if (!proyectoSeleccionado || !proyectoData) {
      return;
    }

    try {
      setGuardandoProyecto(true);
      setErrorProyecto(null);

      // Unir dirección y Google Maps antes de enviar y asegurar el guardado del horario de atención
      const proyectoDataParaEnviar = {
        ...proyectoData,
        direccion_proyecto: unirDireccionYGoogleMaps(
          proyectoData.direccion_proyecto_texto || '',
          proyectoData.direccion_proyecto_maps || ''
        ),
        direccion_sala_ventas: unirDireccionYGoogleMaps(
          proyectoData.direccion_sala_ventas_texto || '',
          proyectoData.direccion_sala_ventas_maps || ''
        ),
        horario_atencion: JSON.stringify(getHorariosArray())
      };

      const response = await updateProyectoInfo(proyectoSeleccionado, proyectoDataParaEnviar);
      if (response.Status === 'Success') {
        // Recargar la información del proyecto para mostrar los datos actualizados
        await recargarProyectoInfo();
      } else {
        setErrorProyecto('Error al guardar la información del proyecto');
      }
    } catch (error) {
      console.error('Error guardando información del proyecto:', error);
      setErrorProyecto(error.message || 'Error al guardar la información del proyecto');
    } finally {
      setGuardandoProyecto(false);
    }
  };

  if (loading) {
    return (
      <div className="main-container container-fluid">
        <div className="breadcrumb-header">
          <Breadcrumb>
            <Breadcrumb.Item>Fine - Tunning</Breadcrumb.Item>
            <Breadcrumb.Item active>Información del negocio</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <Row>
          <Col lg={12} md={12} sm={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" role="status" className="me-3">
                  <span className="visually-hidden">Cargando...</span>
                </Spinner>
                <span>Cargando información de la empresa...</span>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-container container-fluid">
        <div className="breadcrumb-header">
          <Breadcrumb>
            <Breadcrumb.Item>Fine - Tunning</Breadcrumb.Item>
            <Breadcrumb.Item active>Información del negocio</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <Row>
          <Col lg={12} md={12} sm={12}>
            <Card>
              <Card.Body>
                <Alert variant="danger">
                  <Alert.Heading>Error al cargar información</Alert.Heading>
                  <p>{error}</p>
                  <Button
                    variant="outline-danger"
                    onClick={() => window.location.reload()}
                  >
                    Reintentar
                  </Button>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="main-container container-fluid">
      <div className="breadcrumb-header">
        <Breadcrumb>
          <Breadcrumb.Item>Fine - Tunning</Breadcrumb.Item>
          <Breadcrumb.Item active>Información del negocio</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <Row>
        <Col lg={12} md={12} sm={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h3>
                    <strong>Información del negocio</strong>
                  </h3>
                  <p className="text-muted mb-0">
                    Gestiona la información de {empresaData?.nombre_empresa || 'tu empresa'} y sus proyectos.
                  </p>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'empresa')}>
                <Nav variant="tabs" className="nav-tabs mb-4 justify-content-end" defaultActiveKey="empresa">
                  <Nav.Item>
                    <Nav.Link eventKey="empresa">
                      <i className="fe fe-briefcase me-2"></i>
                      Información de la Empresa
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="proyecto">
                      <i className="fe fe-folder me-2"></i>
                      Información de Proyectos
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  {/* Tab de Empresa */}
                  <Tab.Pane eventKey="empresa">
                    <Form>
                      <div className="form-section">
                        <h5 className="fs-6 fw-bold mb-3">Datos de contacto</h5>

                        <Form.Group className="mb-3">
                          <Form.Label>Razón social</Form.Label>
                          <Form.Control
                            type="text"
                            value={empresaData?.razon_social || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>RUC</Form.Label>
                          <Form.Control
                            type="text"
                            value={empresaData?.ruc || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>

                        <Row>
                          <Col md={8}>
                            <Form.Group className="mb-3">
                              <Form.Label>Dirección</Form.Label>
                              <Form.Control
                                type="text"
                                value={empresaData?.direccion || ''}
                                placeholder="No especificado"
                                readOnly
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Departamento</Form.Label>
                              <Form.Control
                                type="text"
                                value={empresaData?.departamento || ''}
                                placeholder="No especificado"
                                readOnly
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={8}>
                            <Form.Group className="mb-3">
                              <Form.Label>Contacto</Form.Label>
                              <Form.Control
                                type="text"
                                value={empresaData?.contacto || ''}
                                placeholder="No especificado"
                                readOnly
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Teléfono de contacto</Form.Label>
                              <Form.Control
                                type="text"
                                value={empresaData?.telefono || ''}
                                placeholder="No especificado"
                                readOnly
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={empresaData?.email || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      </div>

                      <div className="form-section">
                        <h5 className="fs-6 fw-bold mb-3">Redes sociales y página web</h5>

                        <Form.Group className="mb-3">
                          <Form.Label>Página web</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.pagina_web || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.pagina_web && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.pagina_web} target="_blank" rel="noopener noreferrer">
                                Visitar sitio web
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Facebook</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.redes_sociales?.facebook || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.redes_sociales?.facebook && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.redes_sociales.facebook} target="_blank" rel="noopener noreferrer">
                                Ver perfil de Facebook
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Instagram</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.redes_sociales?.instagram || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.redes_sociales?.instagram && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.redes_sociales.instagram} target="_blank" rel="noopener noreferrer">
                                Ver perfil de Instagram
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>WhatsApp</Form.Label>
                          <Form.Control
                            type="text"
                            value={empresaData?.redes_sociales?.whatsapp || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Youtube</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.redes_sociales?.youtube || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.redes_sociales?.youtube && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.redes_sociales.youtube} target="_blank" rel="noopener noreferrer">
                                Ver canal de YouTube
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>LinkedIn</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.redes_sociales?.linkedin || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.redes_sociales?.linkedin && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.redes_sociales.linkedin} target="_blank" rel="noopener noreferrer">
                                Ver perfil de LinkedIn
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>TikTok</Form.Label>
                          <Form.Control
                            type="url"
                            value={empresaData?.redes_sociales?.tiktok || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                          {empresaData?.redes_sociales?.tiktok && (
                            <Form.Text className="text-muted">
                              <a href={empresaData.redes_sociales.tiktok} target="_blank" rel="noopener noreferrer">
                                Ver perfil de TikTok
                              </a>
                            </Form.Text>
                          )}
                        </Form.Group>
                      </div>

                      <div className="form-section">
                        <h5 className="fs-6 fw-bold mb-3">Historia de la marca</h5>
                        <p className="text-muted">
                          Historia y trayectoria de la empresa.
                        </p>
                        <Form.Group className="mb-3">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={empresaData?.historia_marca || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      </div>

                      <div className="form-section">
                        <h5 className="fs-6 fw-bold mb-3">Personalidad de la marca</h5>
                        <p className="text-muted">
                          Características y valores que definen la personalidad de la empresa.
                        </p>
                        <Form.Group className="mb-3">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={empresaData?.personalidad_marca || ''}
                            placeholder="No especificado"
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      </div>

                      <div className="form-buttons">
                        <Button
                          variant="secondary"
                          className="me-2"
                          onClick={() => window.location.reload()}
                        >
                          Actualizar
                        </Button>
                        <Button variant="outline-primary" disabled>
                          Solo lectura
                        </Button>
                      </div>
                    </Form>
                  </Tab.Pane>

                  {/* Tab de Proyecto */}
                  <Tab.Pane eventKey="proyecto">

                    {errorCargarProyectos && (
                      <Alert variant="danger" className="mb-3">
                        {errorCargarProyectos}
                      </Alert>
                    )}

                    {errorProyecto && (
                      <Alert variant="danger" className="mb-3">
                        {errorProyecto}
                      </Alert>
                    )}

                    <div className="form-section mb-4">
                      <h5 className="fs-6 fw-bold mb-3">Seleccionar proyecto</h5>
                      <Form.Group className="mb-3">
                        <Form.Select
                          value={proyectoSeleccionado || ''}
                          onChange={handleProyectoChange}
                          disabled={loadingProyecto || !!errorCargarProyectos}
                          size="lg"
                        >
                          <option value="">Seleccionar proyecto</option>
                          {proyectos.map((proyecto) => {
                            const codigo = proyecto.codigoProyecto || proyecto.codigo_proyecto || proyecto.id;
                            const nombre = proyecto.nombreProyecto || proyecto.nombre_proyecto || proyecto.nombre || `Proyecto ${codigo}`;
                            return (
                              <option
                                key={codigo}
                                value={codigo}
                              >
                                {nombre}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {loadingProyecto && (
                      <div className="text-center py-4">
                        <Spinner animation="border" role="status" className="me-3">
                          <span className="visually-hidden">Cargando...</span>
                        </Spinner>
                        <span>Cargando información del proyecto...</span>
                      </div>
                    )}

                    {!loadingProyecto && proyectoSeleccionado && proyectoData && (
                      <Form>
                        <div className="form-section">
                          <h5 className="fs-6 fw-bold mb-3">Acerca del proyecto</h5>
                          <Form.Group className="mb-3">
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={proyectoData.acerca_proyecto || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 600) {
                                  handleProyectoFieldChange('acerca_proyecto', value);
                                }
                              }}
                              placeholder="Ingrese información sobre el proyecto"
                              maxLength={600}
                            />
                            <Form.Text className="text-muted d-block text-end">
                              {(proyectoData.acerca_proyecto || '').length} / 600 caracteres
                            </Form.Text>
                          </Form.Group>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Fecha estimada de entrega</Form.Label>
                              <Form.Control
                                type="text"
                                value={proyectoData.fecha_estimada_entrega || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 56) {
                                    handleProyectoFieldChange('fecha_estimada_entrega', value);
                                  }
                                }}
                                placeholder="Ej: diciembre 2026"
                                maxLength={56}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.fecha_estimada_entrega || '').length} / 56 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Tipos de unidades que ofrece</Form.Label>
                              <Form.Select
                                value={proyectoData.tipos_unidades || ''}
                                onChange={(e) => handleProyectoFieldChange('tipos_unidades', e.target.value)}
                              >
                                <option value="">Seleccionar</option>
                                <option value="departamentos">Departamentos</option>
                                <option value="lotes">Lotes</option>
                                <option value="departamentos_lotes">Departamentos y lotes</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold d-flex align-items-center">
                                Exhibición de las unidades
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-exhibicion" className="tooltip-exhibicion">
                                      <div className="tooltip-content">
                                        <ul className="mb-0 mt-2 ps-3">
                                          <li><strong>Real:</strong> Unidades habilitadas disponibles para visitar.</li>
                                          <li><strong>Piloto:</strong> Unidades modelo de referencia disponible para visitar.</li>
                                          <li><strong>Showroom:</strong> Solo recursos digitales en sala de ventas.</li>
                                          <li><strong>No disponible:</strong> Sin exhibición física ni digital, solo información comercial.</li>
                                        </ul>
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className="info-icon ms-2" role="button" tabIndex={0}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                      <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="currentColor" fontFamily="Arial, sans-serif">?</text>
                                    </svg>
                                  </span>
                                </OverlayTrigger>
                              </Form.Label>
                              <Form.Select
                                value={proyectoData.exhibicion_unidades || ''}
                                onChange={(e) => handleProyectoFieldChange('exhibicion_unidades', e.target.value)}
                              >
                                <option value="">Seleccionar</option>
                                <option value="real">Real</option>
                                <option value="piloto">Piloto</option>
                                <option value="showroom">Showroom</option>
                                <option value="no_disponible">No disponible</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Etapa actual del proyecto</Form.Label>
                              <Form.Control
                                type="text"
                                value={proyectoData.etapa_actual || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 40) {
                                    handleProyectoFieldChange('etapa_actual', value);
                                  }
                                }}
                                placeholder="Ingrese la etapa actual"
                                maxLength={40}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.etapa_actual || '').length} / 40 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Dirección del proyecto</Form.Label>
                              <Form.Control
                                type="text"
                                value={proyectoData.direccion_proyecto_texto || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 140) {
                                    handleProyectoFieldChange('direccion_proyecto_texto', value);
                                  }
                                }}
                                placeholder="Ingrese la dirección del proyecto"
                                maxLength={140}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.direccion_proyecto_texto || '').length} / 140 caracteres
                              </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <div className="input-group">
                                <span className="input-group-text google-maps-prefix">Google maps:</span>
                                <Form.Control
                                  type="url"
                                  className="google-maps-input"
                                  value={proyectoData.direccion_proyecto_maps || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 200) {
                                      handleProyectoFieldChange('direccion_proyecto_maps', value);
                                    }
                                  }}
                                  placeholder="https://maps.app.goo.gl/..."
                                  maxLength={200}
                                />
                              </div>
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.direccion_proyecto_maps || '').length} / 200 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Dirección de sala de ventas (showroom)</Form.Label>
                              <Form.Control
                                type="text"
                                value={proyectoData.direccion_sala_ventas_texto || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 140) {
                                    handleProyectoFieldChange('direccion_sala_ventas_texto', value);
                                  }
                                }}
                                placeholder="Ingrese la dirección de la sala de ventas"
                                maxLength={140}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.direccion_sala_ventas_texto || '').length} / 140 caracteres
                              </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <div className="input-group">
                                <span className="input-group-text google-maps-prefix">Google maps:</span>
                                <Form.Control
                                  type="url"
                                  className="google-maps-input"
                                  value={proyectoData.direccion_sala_ventas_maps || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 200) {
                                      handleProyectoFieldChange('direccion_sala_ventas_maps', value);
                                    }
                                  }}
                                  placeholder="https://maps.app.goo.gl/..."
                                  maxLength={200}
                                />
                              </div>
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.direccion_sala_ventas_maps || '').length} / 200 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Horario de atención al público</Form.Label>
                              {(() => {
                                const horarios = getHorariosArray();
                                const diasSemana = [
                                  { value: 0, label: 'Domingo' },
                                  { value: 1, label: 'Lunes' },
                                  { value: 2, label: 'Martes' },
                                  { value: 3, label: 'Miércoles' },
                                  { value: 4, label: 'Jueves' },
                                  { value: 5, label: 'Viernes' },
                                  { value: 6, label: 'Sábado' }
                                ];

                                return (
                                  <div>
                                    {horarios.map((horario, index) => (
                                      <div key={index} className="horario-unified-block mb-3">
                                        <div className="horario-controls">
                                          <Form.Select
                                            className="horario-select"
                                            value={horario.num_dia_semana_inicio}
                                            onChange={(e) => handleHorarioChange(index, 'num_dia_semana_inicio', parseInt(e.target.value))}
                                          >
                                            {diasSemana.map(dia => (
                                              <option key={dia.value} value={dia.value}>{dia.label}</option>
                                            ))}
                                          </Form.Select>
                                          <span className="horario-separator">a</span>
                                          <Form.Select
                                            className="horario-select"
                                            value={horario.num_dia_semana_fin}
                                            onChange={(e) => handleHorarioChange(index, 'num_dia_semana_fin', parseInt(e.target.value))}
                                          >
                                            {diasSemana.map(dia => (
                                              <option key={dia.value} value={dia.value}>{dia.label}</option>
                                            ))}
                                          </Form.Select>
                                          <span className="horario-separator">de</span>
                                          <Form.Control
                                            type="time"
                                            className="horario-time"
                                            value={horario.hora_inicio}
                                            onChange={(e) => handleHorarioChange(index, 'hora_inicio', e.target.value)}
                                          />
                                          <span className="horario-separator">a</span>
                                          <Form.Control
                                            type="time"
                                            className="horario-time"
                                            value={horario.hora_fin}
                                            onChange={(e) => handleHorarioChange(index, 'hora_fin', e.target.value)}
                                          />
                                          {horarios.length > 1 && (
                                            <Button
                                              variant="link"
                                              size="sm"
                                              onClick={() => handleEliminarHorario(index)}
                                              className="btn-eliminar-horario ms-2 p-0"
                                              style={{ color: '#dc3545', textDecoration: 'none' }}
                                            >
                                              <i className="fe fe-x"></i>
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleAñadirHorario();
                                      }}
                                      className="text-primary text-decoration-none mt-2 d-inline-block"
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className="fe fe-plus me-1"></i>
                                      Añadir horario
                                    </a>
                                  </div>
                                );
                              })()}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">URL Recorrido Virtual</Form.Label>
                              <Form.Control
                                type="url"
                                value={proyectoData.recorrido_virtual || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 140) {
                                    handleProyectoFieldChange('recorrido_virtual', value);
                                  }
                                }}
                                placeholder="Ingrese la URL del recorrido virtual"
                                maxLength={140}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.recorrido_virtual || '').length} / 140 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Unidades disponibles</Form.Label>
                              <Form.Control
                                type="text"
                                value={proyectoData.unidades_disponibles || ''}
                                onChange={handleUnidadesDisponiblesChange}
                                placeholder="Ingrese las unidades disponibles"
                                maxLength={310}
                              />
                              <Form.Text className="text-muted d-block text-end">
                                {(proyectoData.unidades_disponibles || '').length} / 310 caracteres
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="form-buttons">
                          <Button
                            variant="primary"
                            onClick={handleGuardarProyecto}
                            disabled={guardandoProyecto}
                          >
                            {guardandoProyecto ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Guardando...
                              </>
                            ) : (
                              'Guardar información del proyecto'
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}

                    {!loadingProyecto && !proyectoSeleccionado && (
                      <Alert variant="info">
                        Por favor, seleccione un proyecto para ver y editar su información.
                      </Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de advertencia para editar unidades disponibles */}
      <Modal show={showAdvertenciaUnidades} onHide={handleCancelarEdicionUnidades} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Advertencia: Edición de Unidades Disponibles</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{'{resumenStock}'}</strong> es una <strong>variable interna del sistema</strong> que contiene el resumen actualizado del stock del proyecto.
          </p>
          <p>
            Si modificas esta información, puede influir directamente en lo que el chatbot responda.
          </p>
          <p className="mb-0">
            <strong>Edita con cuidado y asegúrate de que los datos sean correctos y coherentes.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelarEdicionUnidades}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={handleAceptarEdicionUnidades}>
            Acepto la responsabilidad
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InformacionNegocio;
